import SwitchRegularField from 'components/forms/switchField/SwitchRegularField';
import TextField from 'components/forms/textField/TextField';
import { VrboPromotionPlan } from 'models/Channels';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import { VrboPromotion } from '../../PropertySettingsMainTab.types';
import { VrboPromotionPlanWrapper } from './PropertyVrboSettings.styles';

type VrboDiscountPromotionPlan = Exclude<
  VrboPromotionPlan,
  VrboPromotionPlan.NEW_LISTING_DISCOUNT
>;

type VrboMemberPromotionPlan = Exclude<
  VrboDiscountPromotionPlan,
  VrboPromotionPlan.MOBILE
>;

export const VRBO_DEFAULT: Record<VrboDiscountPromotionPlan, number> = {
  [VrboPromotionPlan.MOBILE]: 10,
  [VrboPromotionPlan.MEMBER_BLUE]: 10,
  [VrboPromotionPlan.MEMBER_SILVER]: 15,
  [VrboPromotionPlan.MEMBER_GOLD]: 20,
};

export const VRBO_MINIMUM: Record<VrboDiscountPromotionPlan, number> = {
  [VrboPromotionPlan.MOBILE]: 1,
  [VrboPromotionPlan.MEMBER_BLUE]: 10,
  [VrboPromotionPlan.MEMBER_SILVER]: 11,
  [VrboPromotionPlan.MEMBER_GOLD]: 12,
};

export const VRBO_MAXIMUM: Record<VrboDiscountPromotionPlan, number> = {
  [VrboPromotionPlan.MOBILE]: 100,
  [VrboPromotionPlan.MEMBER_BLUE]: 73,
  [VrboPromotionPlan.MEMBER_SILVER]: 74,
  [VrboPromotionPlan.MEMBER_GOLD]: 75,
};

export const sortVRBOPromotions = (a: VrboPromotion, b: VrboPromotion) => {
  const order = [
    VrboPromotionPlan.MEMBER_BLUE,
    VrboPromotionPlan.MEMBER_SILVER,
    VrboPromotionPlan.MEMBER_GOLD,
    VrboPromotionPlan.MOBILE,
  ];
  return order.indexOf(a.type) - order.indexOf(b.type);
};

const PropertyVrboSettingsPromotionsField = () => {
  const { setValue } = useFormContext();
  const { t } = useTranslation();
  const allPromotions: VrboPromotion[] =
    useWatch({ name: 'vrboSettings.promotions' }) || [];
  const promotions = allPromotions.filter(
    (promotion) => promotion.type !== VrboPromotionPlan.NEW_LISTING_DISCOUNT,
  );
  const isMemberPromotionEnabled = promotions?.some(
    (promo: VrboPromotion) =>
      (promo.type === VrboPromotionPlan.MEMBER_BLUE &&
        promo.percentage !== null) ||
      (promo.type === VrboPromotionPlan.MEMBER_SILVER &&
        promo.percentage !== null) ||
      (promo.type === VrboPromotionPlan.MEMBER_GOLD &&
        promo.percentage !== null),
  );
  const isMobilePromotionEnabled = promotions?.some(
    (promo: VrboPromotion) =>
      promo.type === VrboPromotionPlan.MOBILE && promo.percentage !== null,
  );
  const isPromotionDisabled = (promotion: VrboPromotion) => {
    // Disable next promotion if the previous one is set to 0%
    const sortedPromotions = promotions
      .filter((promo) => promo.type !== VrboPromotionPlan.MOBILE)
      .sort(sortVRBOPromotions);

    const promotionIndex = sortedPromotions.findIndex(
      (promo) => promo.type === promotion.type,
    );

    if (promotionIndex > 0) {
      const previousPromotion: VrboPromotion =
        sortedPromotions[promotionIndex - 1];

      if (
        !previousPromotion.percentage ||
        previousPromotion.percentage <
          VRBO_MINIMUM[previousPromotion.type as VrboDiscountPromotionPlan]
      ) {
        return true;
      }
    }

    return false;
  };

  const onMemberPromotionChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const updatedPromotions = allPromotions.map((promo) => {
      if (
        promo.type === VrboPromotionPlan.MOBILE ||
        promo.type === VrboPromotionPlan.NEW_LISTING_DISCOUNT
      ) {
        return { ...promo };
      }

      return {
        ...promo,
        percentage: evt.target.checked
          ? promo.percentage ??
            VRBO_DEFAULT[promo.type as VrboDiscountPromotionPlan]
          : null,
      };
    });

    setValue('vrboSettings.promotions', updatedPromotions, {
      shouldDirty: true,
    });
  };

  const onMobilePromotionChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const updatedPromotions = allPromotions.map((promo) => {
      if (promo.type !== VrboPromotionPlan.MOBILE) {
        return { ...promo };
      }

      return {
        ...promo,
        percentage: evt.target.checked
          ? promo.percentage ?? VRBO_DEFAULT[VrboPromotionPlan.MOBILE]
          : null,
      };
    });

    setValue('vrboSettings.promotions', updatedPromotions, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <SwitchRegularField
        onChange={onMobilePromotionChange}
        colSmInput={7}
        checked={isMobilePromotionEnabled}
        name="vrboSettings.mobileDiscount"
        colSmLabel={5}
        label={t('pageProperty.mainSettings.vrboSettings.mobileDiscount')}
        rightTooltip={
          <TooltipContentWithLearnMore
            labelTooltipText={t(
              'pageProperty.mainSettings.vrboSettings.mobileDiscountTooltip',
            )}
            learnMoreUrl="https://help.hostfully.com/en/articles/9414677-give-discounts-to-vrbo-guests"
          />
        }
      />

      {isMobilePromotionEnabled &&
        allPromotions?.map((promotion: VrboPromotion, index: number) =>
          promotion.type === VrboPromotionPlan.MOBILE ? (
            <TextField
              type="number"
              key={promotion.uid || index}
              name={`vrboSettings.promotions[${index}].percentage`}
              colSmInput={5}
              colSmOffsetInput={5}
              rightAddons={['%']}
            />
          ) : null,
        )}

      <SwitchRegularField
        onChange={onMemberPromotionChange}
        colSmInput={7}
        checked={isMemberPromotionEnabled}
        name="vrboSettings.memberDiscount"
        colSmLabel={5}
        label={t('pageProperty.mainSettings.vrboSettings.memberDiscount')}
        rightTooltip={
          <TooltipContentWithLearnMore
            labelTooltipText={t(
              'pageProperty.mainSettings.vrboSettings.memberDiscountTooltip',
            )}
            learnMoreUrl="https://help.hostfully.com/en/articles/9414677-give-discounts-to-vrbo-guests"
          />
        }
      />

      <VrboPromotionPlanWrapper>
        {isMemberPromotionEnabled &&
          [...promotions]
            .sort(sortVRBOPromotions)
            .map((promotion: VrboPromotion) => {
              if (promotion.type === VrboPromotionPlan.MOBILE) {
                return null;
              }

              const formIndex = allPromotions.findIndex(
                (promo) => promo.type === promotion.type,
              );

              if (formIndex < 0) {
                return null;
              }

              return (
                <TextField
                  type="number"
                  disabled={isPromotionDisabled(promotion)}
                  key={promotion.uid || promotion.type}
                  name={`vrboSettings.promotions[${formIndex}].percentage`}
                  colSmInput={5}
                  colSmOffsetInput={5}
                  rightAddons={['%']}
                  leftAddon={t(
                    `pageProperty.mainSettings.vrboSettings.promotionPlan.${
                      promotion.type as VrboMemberPromotionPlan
                    }`,
                  )}
                />
              );
            })}
      </VrboPromotionPlanWrapper>
    </>
  );
};

export default PropertyVrboSettingsPromotionsField;
